import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { Icon, SIZES } from '@eventbrite/legacy-eds.icon';
import { translationPropType } from '@eventbrite/legacy-infra.i18n';

import { Button, STYLE_NONE as BUTTON_STYLE_NONE } from '@eventbrite/legacy-eds.button';
import { STYLE_PROP_TYPE, ICON_PROP_TYPE, STYLE_NEUTRAL, STYLE_DARK } from './constants';
import { getAdditionalProps } from '@eventbrite/legacy-eds.utils/props';

import styles from './IconButton.module.scss';

export class IconButton extends PureComponent {
    static propTypes = {
        /**
         * The title used to describe icon button
         */
        title: translationPropType.isRequired,
        /**
         * Changes the style of the icon button.
         */
        style: STYLE_PROP_TYPE,
        /**
         * The type of icon that you want to display (chunky or third party)
         */
        iconType: ICON_PROP_TYPE,
        /**
         * The className to add to icon that you want to display
         */
        iconClassName: PropTypes.string,
        /**
         * The className to add to button
         */
        buttonClassName: PropTypes.string,
        /**
         * If the icon button is active
         **/
        isActive: PropTypes.bool,
        /**
         * If the icon button should be set to a disabled state
         **/
        isDisabled: PropTypes.bool,
        /**
         * If the icon button will be shown over an image, use a style that includes a background color for better visibility
         **/
        isOverImage: PropTypes.bool,
        /**
         * Callback function invoked when the button is clicked.
         */
        onClick: PropTypes.func,
        /**
         * Overrides the color of the icon.
         */
        iconColor: PropTypes.string,
        /**
         * Additional attributes to be passed to the span element.
         */
        spanAttrs: PropTypes.object,
    };

    static defaultProps = {
        style: STYLE_NEUTRAL,
        isActive: false,
        isDisabled: false,
        isOverImage: false,
        spanAttrs: {},
    };

    render() {
        const {
            style,
            title,
            isActive,
            isDisabled,
            isOverImage,
            iconType,
            onClick,
            iconColor,
            iconClassName,
            buttonClassName,
            spanAttrs,
        } = this.props;

        const extraAttrs = getAdditionalProps(this);
        const iconExtraAttrs = {};

        if (buttonClassName) {
            extraAttrs.__containerClassName = buttonClassName;
        }

        if (iconClassName) {
            iconExtraAttrs.__containerClassName = iconClassName;
        }

        let buttonStyle = style;

        if (buttonStyle === STYLE_DARK) {
            buttonStyle = STYLE_NEUTRAL;
        }

        const iconButtonClasses = classNames(styles.root, {
            [styles.active]: isActive,
            [styles.disabled]: isDisabled,
            [styles.overImage]: isOverImage,
            [styles[buttonStyle]]: buttonStyle,
        });

        return (
            <span className={iconButtonClasses} data-testid="icon-button" {...spanAttrs}>
                <Button
                    style={BUTTON_STYLE_NONE}
                    onClick={onClick}
                    disabled={isDisabled}
                    {...extraAttrs}
                >
                    <Icon
                        type={iconType}
                        title={title}
                        isBlockLevel={true}
                        color={iconColor}
                        {...iconExtraAttrs}
                    />
                </Button>
            </span>
        );
    }
}
